import apiClient from '@src/api/clients';

const BASE_URL = 'creative-studio/';

const cleanParams = (params = {}) =>
  Object.fromEntries(
    Object.entries(params).filter(([, value]) => value !== undefined && value !== null && value !== '')
  );

const listResource = (resource, params = {}) =>
  apiClient.request({
    url: `${BASE_URL}${resource}/`,
    method: 'GET',
    payload: cleanParams(params),
  });

const createResource = (resource, payload, options = {}) =>
  apiClient.request({
    url: `${BASE_URL}${resource}/`,
    method: 'POST',
    payload,
    multipart: options.multipart || false,
  });

const updateResource = (resource, id, payload, options = {}) =>
  apiClient.request({
    url: `${BASE_URL}${resource}/${id}/`,
    method: 'PATCH',
    payload,
    multipart: options.multipart || false,
  });

const renderAdVariant = (id) =>
  apiClient.request({
    url: `${BASE_URL}ad-variants/${id}/render/`,
    method: 'POST',
    payload: {},
  });

export const listHookTemplates = (params = {}) => listResource('hook-templates', params);
export const createHookTemplate = (payload) => createResource('hook-templates', payload);
export const updateHookTemplate = (id, payload) => updateResource('hook-templates', id, payload);

export const listHookAssets = (params = {}) => listResource('hook-assets', params);
export const createHookAsset = (payload) => createResource('hook-assets', payload, { multipart: true });

export const listBodyVideoAssets = (params = {}) => listResource('body-video-assets', params);
export const createBodyVideoAsset = (payload) => createResource('body-video-assets', payload, { multipart: true });

export const listCTAAssets = (params = {}) => listResource('cta-assets', params);
export const createCTAAsset = (payload) => createResource('cta-assets', payload, { multipart: true });

export const listVoiceoverAssets = (params = {}) => listResource('voiceover-assets', params);
export const createVoiceoverAsset = (payload) => createResource('voiceover-assets', payload, { multipart: true });

export const listAdVariants = (params = {}) => listResource('ad-variants', params);
export const createAdVariant = (payload) => createResource('ad-variants', payload, { multipart: true });
export const updateAdVariant = (id, payload) => updateResource('ad-variants', id, payload, { multipart: true });

export const listRenderJobs = (params = {}) => listResource('render-jobs', params);

// ---------------------------------------------------------------------------
// Creative Task Workflow
// ---------------------------------------------------------------------------

export const listAvatars = (params = {}) => listResource('avatars', params);
export const createAvatar = (payload) => createResource('avatars', payload);
export const updateAvatar = (id, payload) => updateResource('avatars', id, payload);

export const listPainPoints = (params = {}) => listResource('pain-points', params);
export const createPainPoint = (payload) => createResource('pain-points', payload);
export const updatePainPoint = (id, payload) => updateResource('pain-points', id, payload);

export const listAngleChoices = (params = {}) => listResource('angle-choices', params);
export const createAngleChoice = (payload) => createResource('angle-choices', payload);
export const updateAngleChoice = (id, payload) => updateResource('angle-choices', id, payload);
export const listStyleChoices = (params = {}) => listResource('style-choices', params);
export const createStyleChoice = (payload) => createResource('style-choices', payload);
export const updateStyleChoice = (id, payload) => updateResource('style-choices', id, payload);
export const listAdTypeChoices = (params = {}) => listResource('ad-type-choices', params);
export const createAdTypeChoice = (payload) => createResource('ad-type-choices', payload);
export const updateAdTypeChoice = (id, payload) => updateResource('ad-type-choices', id, payload);
export const listDesignerProfiles = (params = {}) => listResource('designer-profiles', params);
export const createDesignerProfile = (payload) => createResource('designer-profiles', payload);
export const updateDesignerProfile = (id, payload) => updateResource('designer-profiles', id, payload);

// Flights
export const listFlights = (params = {}) => listResource('flights', params);
export const fetchFlight = (id) =>
  apiClient.request({ url: `${BASE_URL}flights/${id}/`, method: 'GET' });
export const createFlight = (payload) => createResource('flights', payload);
export const updateFlight = (id, payload) => updateResource('flights', id, payload);
export const deleteFlight = (id) =>
  apiClient.request({ url: `${BASE_URL}flights/${id}/`, method: 'DELETE' });
export const generateFlightTasks = (flightId, payload) =>
  apiClient.request({ url: `${BASE_URL}flights/${flightId}/generate-tasks/`, method: 'POST', payload });

// Tasks
export const listCreativeTasks = (params = {}) => listResource('tasks', params);
export const bulkUpdateTasks = (payload) =>
  apiClient.request({ url: `${BASE_URL}tasks/bulk-update/`, method: 'POST', payload });
export const generateScript = (taskId, payload = {}) =>
  apiClient.request({ url: `${BASE_URL}tasks/${taskId}/generate-script/`, method: 'POST', payload });

// Script Guardrails
export const listScriptGuardrails = (params = {}) => listResource('script-guardrails', params);
export const createScriptGuardrail = (payload) => createResource('script-guardrails', payload);
export const updateScriptGuardrail = (id, payload) => updateResource('script-guardrails', id, payload);

// Script Prompt Templates
export const listScriptPrompts = (params = {}) => listResource('script-prompts', params);
export const updateScriptPrompt = (id, payload) => updateResource('script-prompts', id, payload);

export const fetchCreativeTask = (id) =>
  apiClient.request({
    url: `${BASE_URL}tasks/${id}/`,
    method: 'GET',
  });

export const createCreativeTask = (payload) => createResource('tasks', payload);
export const updateCreativeTask = (id, payload) => updateResource('tasks', id, payload);

export const deleteCreativeTask = (id) =>
  apiClient.request({
    url: `${BASE_URL}tasks/${id}/`,
    method: 'DELETE',
  });

export const assignCreativeTask = (id) =>
  apiClient.request({ url: `${BASE_URL}tasks/${id}/assign/`, method: 'POST', payload: {} });

export const startCreativeTask = (id) =>
  apiClient.request({ url: `${BASE_URL}tasks/${id}/start/`, method: 'POST', payload: {} });

export const submitCreativeTask = (id) =>
  apiClient.request({ url: `${BASE_URL}tasks/${id}/submit/`, method: 'POST', payload: {} });

export const requestRevisionCreativeTask = (id, payload) =>
  apiClient.request({ url: `${BASE_URL}tasks/${id}/request-revision/`, method: 'POST', payload });

export const approveCreativeTask = (id) =>
  apiClient.request({ url: `${BASE_URL}tasks/${id}/approve/`, method: 'POST', payload: {} });

export const fetchCreativeTaskComments = (id) =>
  apiClient.request({ url: `${BASE_URL}tasks/${id}/comments/`, method: 'GET' });

export const addCreativeTaskComment = (id, payload) =>
  apiClient.request({ url: `${BASE_URL}tasks/${id}/comments/`, method: 'POST', payload });

export const uploadCreativeTaskFile = (id, formData) =>
  apiClient.request({
    url: `${BASE_URL}tasks/${id}/upload/`,
    method: 'POST',
    payload: formData,
    multipart: true,
  });

export default {
  listHookTemplates,
  createHookTemplate,
  updateHookTemplate,
  listHookAssets,
  createHookAsset,
  listBodyVideoAssets,
  createBodyVideoAsset,
  listCTAAssets,
  createCTAAsset,
  listVoiceoverAssets,
  createVoiceoverAsset,
  listAdVariants,
  createAdVariant,
  updateAdVariant,
  listRenderJobs,
  renderAdVariant,
  listAvatars,
  createAvatar,
  updateAvatar,
  listPainPoints,
  createPainPoint,
  updatePainPoint,
  listAngleChoices,
  createAngleChoice,
  updateAngleChoice,
  listStyleChoices,
  createStyleChoice,
  updateStyleChoice,
  listDesignerProfiles,
  createDesignerProfile,
  updateDesignerProfile,
  listCreativeTasks,
  fetchCreativeTask,
  createCreativeTask,
  updateCreativeTask,
  deleteCreativeTask,
  startCreativeTask,
  submitCreativeTask,
  requestRevisionCreativeTask,
  approveCreativeTask,
  fetchCreativeTaskComments,
  addCreativeTaskComment,
  uploadCreativeTaskFile,
};
